import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopNavbar from "../top-navbar/TopNav";
import { getQuickBGVCandidateInfo } from "../../services/employee/api";
// Updated: Added FaArrowLeft to icon imports
import { FaIdCard, FaBriefcase, FaShieldAlt, FaChevronRight, FaArrowLeft } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../styles/employee/candidate-details.css";

const QuickBGVDash = () => {
  const { id } = useParams();
  const token = localStorage.getItem("bgc_token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [candidateData, setCandidateData] = useState(null);
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/employee/quickbgv'); 
  };

  const fetchCandidateStatus = useCallback(async () => {
    if (!id || !token) {
      setError("Missing candidate ID or token");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await getQuickBGVCandidateInfo(id);
      localStorage.setItem("QuickBGC_candidateID", response.data.candidateId);

      if (response?.data) {
        setCandidateData(response.data);
      } else {
        setError("Failed to fetch candidate details.");
      }
    } catch (err) {
      toast.error(err);
      setError("Failed to fetch candidate details.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchCandidateStatus();
  }, [fetchCandidateStatus]);

  const isCompleted = (status) => status?.toUpperCase() === "COMPLETED";

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "quickdash-status-completed";

      case "PENDING":
        return "quickdash-status-pending";

      case "WORK IN PROGRESS":
      case "IN PROGRESS":
      case "INPROGRESS":
        return "quickdash-status-work-in-progress";

      default:
        return "quickdash-status-pending";
    }
  };

  const getDisplayStatus = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "Completed";

      case "PENDING":
        return "Pending";

      case "WORK IN PROGRESS":
      case "IN PROGRESS":
      case "INPROGRESS":
        return "Work In Progress";

      default:
        return "Pending";
    }
  };

  if (loading) {
    return (
      <div className="centered-message">
        <div className="spinner"></div>
        <div>Loading QuickBGV Dashboard ...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="centered-message">
        <div>Error: {error}</div>
      </div>
    );
  }

  const handleCheckClick = (status, route) => {
    if (isCompleted(status)) {
      return;
    }

    navigate(route);
  };

  if (!candidateData) {
    return (
      <div className="centered-message">
        <div>No candidate information available.</div>
      </div>
    );
  }

  const { candidateId, uanStatus, aadhaarStatus, criminalStatus } =
    candidateData;

  return (
    <div className="dashboard-app-container">
      <Sidebar />

      <div className="dashboard-right-panel">
        <TopNavbar />

        <div className="Quick-candidate-details-container">
          
          {/* Candidate Info Header with Back Button */}
          <div className="quickdash-candidate-info-header d-flex align-items-center justify-content-between p-3">
            <div className="quickdash-candidate-info-left d-flex align-items-center gap-3">
              
              {/* Integrated Back Button */}
              <button
                type="button"
                className="quick-btn-back-custom"
                onClick={handleBack}
                title="Go Back"
              >
                <FaArrowLeft size={16} />
              </button>

              <div className="quickdash-candidate-info-icon">
                <i className="fas fa-user"></i>
              </div>

              <div className="quickdash-candidate-info-text">
                <span>Candidate ID</span>
                <strong>{candidateId}</strong>
              </div>
            </div>

            <div className="quickdash-candidate-info-badge">
              <i className="fas fa-shield-alt"></i>
              BGV Candidate
            </div>
          </div>

          <div className="quickdash-candidate-checks-grid px-3 py-5">
            {/* Aadhaar Check */}
            <div
              className={`quickdash-check-card quickdash-check-card-adjust ${getStatusClass(aadhaarStatus)}`}
              onClick={() =>
                handleCheckClick(aadhaarStatus, `/employee/aadhaarcheck/${id}`)
              }
              style={{
                cursor: isCompleted(aadhaarStatus) ? "not-allowed" : "pointer",
              }}
            >
              <div className="quickdash-check-icon quickdash-aadhaar-icon">
                <FaIdCard />
              </div>

              <div className="quickdash-check-card-content">
                <div className="quickdash-check-name">Aadhaar Check</div>
                <span className={`quickdash-check-status ${getStatusClass(aadhaarStatus)}`}>
                  {getDisplayStatus(aadhaarStatus)}
                </span>
              </div>

              <div className="quickdash-check-arrow">
                <FaChevronRight />
              </div>
            </div>

            {/* UAN Check */}
            <div
              className={`quickdash-check-card quickdash-check-card-adjust ${getStatusClass(uanStatus)}`}
              onClick={() =>
                handleCheckClick(uanStatus, `/employee/quickuancheck/${id}`)
              }
              style={{
                cursor: isCompleted(uanStatus) ? "not-allowed" : "pointer",
              }}
            >
              <div className="quickdash-check-icon quickdash-uan-icon">
                <FaBriefcase />
              </div>

              <div className="quickdash-check-card-content">
                <div className="quickdash-check-name">UAN Check</div>
                <span className={`quickdash-check-status ${getStatusClass(uanStatus)}`}>
                  {getDisplayStatus(uanStatus)}
                </span>
              </div>

              <div className="quickdash-check-arrow">
                <FaChevronRight />
              </div>
            </div>

            {/* Criminal Check */}
            <div
              className={`quickdash-check-card quickdash-check-card-adjust ${getStatusClass(criminalStatus)}`}
              onClick={() =>
                handleCheckClick(
                  criminalStatus,
                  `/employee/quickcriminalcheck/${id}`
                )
              }
              style={{
                cursor: isCompleted(criminalStatus) ? "not-allowed" : "pointer",
              }}
            >
              <div className="quickdash-check-icon quickdash-criminal-icon">
                <FaShieldAlt />
              </div>

              <div className="quickdash-check-card-content">
                <div className="quickdash-check-name">Criminal Check</div>
                <span className={`quickdash-check-status ${getStatusClass(criminalStatus)}`}>
                  {getDisplayStatus(criminalStatus)}
                </span>
              </div>

              <div className="quickdash-check-arrow">
                <FaChevronRight />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickBGVDash;